import React, { FC, ReactNode, useContext, useEffect, useState } from 'react';
import cx from 'classnames';
import { useDrag } from '@use-gesture/react';

import { LocaleContext } from '@/contexts/locale-context';

/**
 * TYPINGS
 */
export type HeaderASlideshowProps = {
	slides: Array<ReactNode>;
	/** Time (ms) a slide stays displayed before the next one comes in */
	delay?: number;
};

type SlideshowState = {
	current: number;
	/** Slide that stays fixed underneath while the current one slides over it */
	previous: number | null;
	/** 1 => the incoming slide comes from the right, -1 => from the left */
	direction: 1 | -1;
};

const ArrowIcon = () => (
	<svg
		width="17.6899"
		height="17.2015"
		viewBox="0 0 17.6899 17.2015"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			d="M8.59423 0.494975L16.7 8.60074L8.59423 16.7065"
			stroke="black"
			strokeWidth="1.4"
		/>
		<path
			d="M15.7456 8.60074H0.7"
			stroke="black"
			strokeWidth="1.4"
			strokeLinecap="square"
		/>
	</svg>
);

const PauseIcon = () => (
	<svg
		width="8"
		height="14"
		viewBox="0 0 8 14"
		xmlns="http://www.w3.org/2000/svg"
	>
		<rect x="0" y="0" width="2" height="14" />
		<rect x="6" y="0" width="2" height="14" />
	</svg>
);

const PlayIcon = () => (
	<svg
		width="12"
		height="14"
		viewBox="0 0 12 14"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path d="M0 0l12 7-12 7z" />
	</svg>
);

/**
 * COMPONENT
 *
 * Slideshow dedicated to the HeaderA right column:
 * - autoplay (paused by default if the user prefers reduced motion)
 * - play/pause button between the prev/next arrows
 * - the active slide stays still, the incoming slide slides over it
 */
export const HeaderASlideshow: FC<HeaderASlideshowProps> = ({
	slides,
	delay = 3000,
}) => {
	const [{ current, previous, direction }, setState] =
		useState<SlideshowState>({ current: 0, previous: null, direction: 1 });
	const [isPlaying, setIsPlaying] = useState(true);

	const { __t } = useContext(LocaleContext);

	const goTo = (index: number, dir: 1 | -1) =>
		setState((state) => ({
			current: (index + slides.length) % slides.length,
			previous: state.current,
			direction: dir,
		}));

	const goNext = () => goTo(current + 1, 1);
	const goPrev = () => goTo(current - 1, -1);

	/**
	 * Don't autoplay if the user prefers reduced motion
	 */
	useEffect(() => {
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			setIsPlaying(false);
		}
	}, []);

	/**
	 * Autoplay (restarts the countdown each time the slide changes,
	 * so a manual navigation gives the new slide its full display time)
	 */
	useEffect(() => {
		if (!isPlaying) return;

		const timer = setTimeout(goNext, delay);
		return () => clearTimeout(timer);
	}, [isPlaying, current, delay]);

	/**
	 * Swipe support
	 */
	const bind = useDrag(
		({ swipe: [swipeX] }) => {
			if (swipeX < 0) goNext();
			else if (swipeX > 0) goPrev();
		},
		{ filterTaps: true }
	);

	return (
		<div
			className="supt-slider supt-headerSlideshow"
			aria-roledescription="carousel"
		>
			<div className="supt-slider__controls">
				<div className="supt-slider__navigation">
					<button
						onClick={goPrev}
						aria-label={__t(
							'slider-navigation-prev-label',
							'Previous slide'
						)}
						className="supt-slider__button supt-slider__button--prev"
					>
						<span>
							<ArrowIcon />
						</span>
					</button>

					<button
						onClick={() => setIsPlaying((playing) => !playing)}
						aria-label={
							isPlaying
								? __t('slider-navigation-pause-label', 'Pause')
								: __t('slider-navigation-play-label', 'Play')
						}
						className="supt-slider__button supt-slider__button--toggle"
					>
						<span>{isPlaying ? <PauseIcon /> : <PlayIcon />}</span>
					</button>

					<button
						onClick={goNext}
						aria-label={__t(
							'slider-navigation-next-label',
							'Next slide'
						)}
						className="supt-slider__button supt-slider__button--next"
					>
						<span>
							<ArrowIcon />
						</span>
					</button>
				</div>
			</div>

			<div
				{...bind()}
				className="supt-slider__container"
				aria-live={isPlaying ? 'off' : 'polite'}
			>
				{slides.map((slide, i) => (
					<div
						key={i}
						className={cx('supt-slider__slide', {
							'is-active': i === current,
							'is-previous': i === previous && i !== current,
							// no animation on the first render
							'is-entering': i === current && previous !== null,
							'from-left': direction === -1,
						})}
						aria-hidden={i !== current}
					>
						{slide}
					</div>
				))}
			</div>
		</div>
	);
};
